import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';

// Services
import { LanguageService } from '../../services/language/language.service';

// RxJS
import { Subscription } from 'rxjs';

// SEO
import { SeoService } from '../../services/seo/seo.service';

// Crypt
// 'js-sha1' ships without type declarations and no matching @types package
// is installed, so noImplicitAny cannot be satisfied with a real type here
// without adding a new dependency.
// @ts-ignore
import * as sha1 from 'js-sha1';
import { PASSWORD_TOKEN_SHA1 } from '../../config/config';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AuthService } from 'src/app/services/auth/auth.service';

/**
 * Declare plugins so then you can run it
 */
declare function init_plugins(): void;

@Component({
  selector: 'app-restore-password-confirm',
  templateUrl: './restore-password-confirm.component.html',
  styleUrls: ['./restore-password-confirm.component.css'],
  imports: [
    RouterLink,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    TranslocoPipe,
  ],
})
export class RestorePasswordConfirmComponent implements OnInit, OnDestroy {
  /**
   * The userEmail variable is declare
   * @param userEmail Variable to save the user’s email
   */
  userEmail = '';
  /**
   * The language variable is declare
   * @param language Variable to save the actual language
   */
  language!: string;
  form!: UntypedFormGroup;
  //* Reset password hash.
  hash = '';
  //* Boolean to handle initial confirmation. By default it must be true.
  isLoading = true;
  //* Boolean to handle error in confirmation.
  isHashInvalid = false;
  //* Boolean to handle password reset loading.
  isLoadingPasswordReset = false;
  //* Boolean to handle password reset.
  isPasswordReset = false;

  private subscription: Subscription | undefined;

  /**
   * Following variables declared
   * @param router Variable that manage the routes
   * @param globalStateService Service that brings the SHA token.
   * @param languageService Service that applies the selected language by the user
   */
  constructor(
    public router: Router,
    private route: ActivatedRoute,
    public languageService: LanguageService,
    private authService: AuthService,
    private seoService: SeoService,
    private title: Title,
    private cdr: ChangeDetectorRef,
    private transloco: TranslocoService,
  ) {}

  /**
   * The plugins run
   */
  ngOnInit() {
    init_plugins();
    this.language = this.languageService.getLanguage();

    // SEO
    const title = this.transloco.translate(
      'RESTORE-PASSWORD-CONFIRM.PASSWORD-RESET',
    );
    this.title.setTitle(title);
    this.seoService.generateTags({
      title: title,
      description: '',
      slug: 'restore-password-confirm',
    });

    this.form = new UntypedFormGroup(
      {
        email: new UntypedFormControl(null, [
          Validators.required,
          Validators.email,
        ]),
        password: new UntypedFormControl(null, [
          Validators.required,
          Validators.minLength(6),
        ]),
        confirmPassword: new UntypedFormControl(null, [
          Validators.required,
          Validators.minLength(6),
        ]),
        conditions: new UntypedFormControl(false),
      },
      {
        validators: this.areEqual('password', 'confirmPassword'),
      },
    );

    this.hash = this.route.snapshot.paramMap.get('hash') || '';
    if (this.hash !== '')
      this.authService.validateResetPasswordHash(this.hash).subscribe({
        next: (_resp) => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (_error) => {
          this.isLoading = false;
          this.isHashInvalid = true;
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  areEqual(passwordKey: string, confirmPasswordKey: string) {
    return (control: AbstractControl) => {
      const group = control as UntypedFormGroup;
      const password = group.controls[passwordKey].value;
      const confirmPassword = group.controls[confirmPasswordKey].value;
      if (password === confirmPassword) return null;
      return { notEqual: true };
    };
  }

  /**
   * Function to change language localStorage
   * @param language Variable to save the actual language
   */
  changeLanguage(language: string) {
    if (language === 'es' || language === 'en') {
      this.language = language;
      this.languageService.applyLanguage(this.language);
      window.location.reload();
    } else return;
  }

  resetPassword() {
    this.isLoadingPasswordReset = true;
    const email = this.form.get('email')?.value?.toLowerCase() || '';
    const password = this.form.get('password')?.value || '';
    const tokenSHA = PASSWORD_TOKEN_SHA1;
    const body = {
      email,
      password: sha1(email + password + tokenSHA),
      hash: this.hash,
      language: '',
    };
    this.authService.resetPassword(body).subscribe({
      next: (_resp) => {
        this.isPasswordReset = true;
        this.isLoadingPasswordReset = false;
        this.cdr.markForCheck();
      },
      error: (_error) => {
        console.log(_error);
        this.isLoadingPasswordReset = false;
        this.cdr.markForCheck();
      },
    });
  }
}
